<template>
  <div class="reservation-summary flex justify-between items-center">
    <div class="price-info">
      <span>{{ $t('total_billing_amount') }}</span>
      <div class="detail flex items-center">
        <span class="price" :class="[isSoldOut ? 'text-gray-60' : 'text-yellow-50']">
          {{ props.product?.currency_code }} {{ priceFormat(totalPrice) }}
        </span>
        <booking-detail-button :product="props.product" :booking-item="bookingItem" :additions="additions" :upgrades="upgrades" />
      </div>
    </div>
    <nd-button type="primary" :disabled="!canNextStep" @click="handleNextStepClick">{{ nextStepBtnText }}</nd-button>
  </div>
</template>

<script setup lang="ts">
import { storeToRefs } from 'pinia'

import { useBookingStore } from '@/store/booking'
import { useAuthStore } from '@/store/auth'

import BookingDetailButton from '@/components/booking/booking-detail-button.vue'

import type { UiProductInfo, UiProductSelectedData } from '@/types/product'
import type { ProductUpgradeResource, ProductAdditionsResource } from '@/types/booking'

import { priceFormat } from '@/utils/price'

const { t } = useI18n()

const router = useLocaleRouter()

const bookingStore = useBookingStore()
const authStore = useAuthStore()

const { product: storeProduct, bookingItem: storeBookingItem, additions: storeAdditions, upgrades: storeUpgrades } = storeToRefs(bookingStore)

const props = defineProps<{
  product: UiProductInfo
  bookingItem: UiProductSelectedData[]
  additions: ProductAdditionsResource[]
  upgrades: ProductUpgradeResource[]
}>()

const isSoldOut = inject<ComputedRef<boolean>>('isSoldOut')

const additionsTotalPrice = computed(() => (props.additions?.length ? props.additions.reduce((acc, a) => acc + Number(a?.amount || 0), 0) : 0))
const upgradesTotalPrice = computed(() => (props.upgrades?.length ? props.upgrades.reduce((acc, u) => acc + Number(u?.amount || 0), 0) : 0))
const totalPrice = computed(() => additionsTotalPrice.value + upgradesTotalPrice.value)

const nextStepBtnText = computed(() => (isSoldOut?.value ? t('all_inventory_status_sold_out') : t('all_next_step')))
const canNextStep = computed(() => !isSoldOut?.value && props.bookingItem?.[0] && props.upgrades?.length)

const updateBookingStore = () => {
  storeProduct.value = props.product
  storeBookingItem.value = props.bookingItem
  storeAdditions.value = props.additions
  storeUpgrades.value = props.upgrades
}
const handleNextStepClick = () => {
  if (!canNextStep.value) return
  if (!authStore.isLogin) return authStore.openLoginModal()

  updateBookingStore()
  router.push({ path: `/booking/${props.product?.pid}/remarks` })
}
</script>

<style lang="scss" scoped>
.reservation-summary {
  container-type: inline-size;
  .price-info {
    @apply flex flex-col items-start text-sm whitespace-nowrap;
    .price {
      @apply text-base font-500;
    }
    button {
      @apply text-sm;
    }
  }
  @container (min-width: 400px) {
    .price-info {
      @apply flex flex-row items-center gap-2 text-lg;
      .price {
        @apply text-lg font-500;
      }
      button {
        @apply text-base;
      }
    }
  }
}
</style>
